// Plasma-themed classNames presets for vortex-auth React components.
//
// Each preset maps a vortex-auth component's classNames slots onto plasma's
// shadcn token classes (the exact strings used by
// `components/ui/{button,input,card,dropdown-menu}.tsx`). Keeping them here
// means every F2 surface themes identically and we change tokens in one place.
//
// Token references (kept in sync with the shadcn primitives):
//   button (default)     -> "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90"
//   button (outline)     -> "border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground"
//   button (destructive) -> "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90"
//   input                -> "border border-input bg-transparent ... focus-visible:ring-ring/50"
//   card                 -> "rounded-xl border bg-card text-card-foreground shadow-sm"
//   dropdown content     -> "rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
//   dropdown item        -> "rounded-sm px-2 py-1.5 text-sm focus:bg-accent focus:text-accent-foreground"
import type {
  AuthFormClassNames,
  VortexAuthPageClassNames,
  VortexAuthSurfaceClassNames,
  VortexOrganizationMembersClassNames,
  VortexOrgProfileClassNames,
  VortexOrgSwitcherClassNames,
  VortexUserButtonClassNames,
  VortexUserProfileClassNames,
} from "@vortexnyc/vortex-core/auth/react";

// Shared atoms — reused across presets so theming stays consistent.
const buttonPrimary =
  "label inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm h-9 px-4 py-2 outline-none transition-[color,box-shadow] bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50";
const buttonOutline =
  "label inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm h-9 px-4 py-2 outline-none transition-[color,box-shadow] border border-border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50";
const buttonDestructive =
  "label inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium text-sm h-9 px-4 py-2 outline-none transition-[color,box-shadow] bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 focus-visible:ring-[3px] focus-visible:ring-destructive disabled:pointer-events-none disabled:opacity-50";
const buttonDisabled = "pointer-events-none opacity-50";
const inputField =
  "flex h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50";
const cardSurface =
  "flex flex-col gap-6 rounded-xl border bg-card py-6 text-card-foreground shadow-sm";
const fieldLabel = "label text-sm text-foreground";
const dropdownPanel =
  "z-50 min-w-[12rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md";
const dropdownItem =
  "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground";
const dropdownDivider = "-mx-1 my-1 h-px bg-muted";
const dropdownSectionTitle =
  "px-2 py-1.5 font-semibold text-muted-foreground text-xs uppercase tracking-wide";

// AuthSignInForm / AuthSignUpForm slot styling (shared by sign-in + sign-up pages).
const authFormClassNames: AuthFormClassNames = {
  card: `${cardSurface} w-full px-6`,
  header: "space-y-1",
  title: "subtitle-sm font-semibold text-xl leading-none text-foreground",
  description: "body-sm text-muted-foreground",
  content: "space-y-4",
  field: "space-y-2",
  label: fieldLabel,
  input: inputField,
  primaryButton: `${buttonPrimary} w-full`,
  secondaryButton: `${buttonOutline} w-full`,
  alert:
    "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive text-sm",
  divider: "flex items-center gap-3 text-muted-foreground text-xs",
  providers: "grid gap-2",
  providerButton: buttonOutline,
  footer: "text-center text-muted-foreground text-sm",
  link: "text-primary underline-offset-4 hover:underline",
};

// VortexAuthSurface — the split-panel chrome around the auth forms.
const authSurfaceClassNames: VortexAuthSurfaceClassNames = {
  root: "flex min-h-dvh items-center justify-center bg-muted p-4",
  grid: "w-full max-w-sm",
  main: "w-full",
  header: "space-y-1",
  title: "subtitle-sm font-semibold text-xl leading-none text-foreground",
  description: "body-sm text-muted-foreground",
};

// Sign-in + sign-up page preset (surface + form slots).
export const signInClassNames: VortexAuthPageClassNames = {
  surface: authSurfaceClassNames,
  form: authFormClassNames,
};

export const signUpClassNames: VortexAuthPageClassNames = signInClassNames;

// VortexUserProfile — account settings (name/image/password/2FA/email/delete).
export const userProfileClassNames: VortexUserProfileClassNames = {
  card: cardSurface,
  header: "px-6",
  title: "subtitle-sm font-semibold leading-none text-foreground",
  description: "body-sm text-muted-foreground",
  body: "space-y-6 px-6",
  field:
    "flex items-center justify-between border-muted-foreground/10 border-b py-2",
  label: "text-muted-foreground",
  value: "text-foreground",
  input: inputField,
  image: "size-20 rounded-xl object-cover",
  imagePlaceholder:
    "flex size-20 items-center justify-center rounded-xl bg-muted text-lg text-muted-foreground",
  actions: "flex flex-wrap gap-2",
  primaryButton: buttonPrimary,
  secondaryButton: buttonOutline,
  dangerButton: buttonDestructive,
  sectionDivider: "border-border border-t",
  sectionTitle: "label text-foreground",
  sectionBody: "space-y-3",
  providerItem:
    "flex items-center justify-between rounded-lg border border-border p-4",
  providerLabel: "label text-foreground",
  errorBanner:
    "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive text-sm",
};

// VortexOrganizationSwitcher — sidebar org switcher.
export const orgSwitcherClassNames: VortexOrgSwitcherClassNames = {
  trigger:
    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50",
  triggerName: "truncate font-medium text-foreground",
  triggerImage: "size-6 rounded-md object-cover",
  triggerPlaceholder:
    "flex size-6 items-center justify-center rounded-md bg-muted text-muted-foreground text-xs",
  dropdown: "min-w-[12rem]",
  dropdownPanel,
  dropdownSection: "py-1",
  dropdownSectionTitle,
  dropdownItem,
  dropdownItemActive: `${dropdownItem} bg-accent text-accent-foreground`,
  dropdownItemLabel: "truncate text-foreground",
  dropdownItemMeta: "text-muted-foreground text-xs",
  dropdownDivider,
  createButton: `${dropdownItem} text-primary`,
};

// VortexUserButton — sidebar user menu.
export const userButtonClassNames: VortexUserButtonClassNames = {
  trigger:
    "flex items-center gap-2 rounded-md px-1 py-1 outline-none transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50",
  avatar: "size-8 rounded-lg object-cover",
  initials:
    "flex size-8 items-center justify-center rounded-lg bg-muted font-medium text-muted-foreground text-sm",
  dropdown: "min-w-[14rem]",
  dropdownPanel,
  dropdownSection: "py-1",
  dropdownSectionTitle,
  dropdownItem,
  dropdownItemActive: `${dropdownItem} bg-accent text-accent-foreground`,
  dropdownItemLabel: "truncate text-foreground",
  dropdownItemMeta: "text-muted-foreground text-xs",
  dropdownDivider,
  signOutButton: `${dropdownItem} text-destructive`,
};

// VortexOrganizationMembersSurface — members list + invite form.
export const orgMembersClassNames: VortexOrganizationMembersClassNames = {
  form: "space-y-4",
  formGrid: "grid gap-3 sm:grid-cols-[1fr_auto_auto]",
  label: "space-y-2",
  labelText: fieldLabel,
  input: inputField,
  select: inputField,
  primaryButton: buttonPrimary,
  primaryButtonDisabled: `${buttonPrimary} ${buttonDisabled}`,
  list: "space-y-3",
  listCard: cardSurface,
  listContent: "space-y-3 px-6",
  listHeader: "px-6",
  memberDetails: "min-w-0",
  memberName: "label truncate text-foreground",
  memberEmail: "caption truncate text-muted-foreground",
  memberMetadata: "text-muted-foreground text-xs",
  status: "shrink-0",
  actions: "flex items-center gap-2",
  secondaryButton: buttonOutline,
  secondaryButtonDisabled: `${buttonOutline} ${buttonDisabled}`,
  stateText: "text-muted-foreground text-sm",
};

// VortexOrganizationProfile — org profile editor.
export const orgProfileClassNames: VortexOrgProfileClassNames = {
  card: cardSurface,
  header: "px-6",
  title: "subtitle-sm font-semibold leading-none text-foreground",
  description: "body-sm text-muted-foreground",
  body: "space-y-6 px-6",
  field:
    "flex items-center justify-between border-muted-foreground/10 border-b py-2",
  label: "text-muted-foreground",
  value: "text-foreground",
  input: inputField,
  image: "size-20 rounded-xl object-cover",
  actions: "flex flex-wrap gap-2",
  primaryButton: buttonPrimary,
  secondaryButton: buttonOutline,
  dangerButton: buttonDestructive,
  statusBadge:
    "inline-flex items-center rounded-md bg-secondary px-2 py-0.5 font-medium text-secondary-foreground text-xs",
};
